"use client";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

const BottomSheet = ({
  isOpen,
  onClose,
  title,
  children,
  className,
  overlayClassName,
  headerClassName,
  borderClassName = "border-[#E9E9EA] border-b",
  titleClassName,
  closeButtonClassName,
  contentClassName,
  innerClassName,
}) => {
  const isMobile = useIsMobile();
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Lock body scroll when open (unlock as soon as closed, like Modal)
  useLockBodyScroll(isOpen && isMobile);

  // Handle mount/unmount with animation timings
  useEffect(() => {
    if (isOpen && isMobile) {
      setShouldRender(true);
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else if (!isOpen && isMobile) {
      setIsAnimating(false);
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isMobile]);

  const overlayClasses = useMemo(
    () =>
      clsx(
        "fixed inset-0 z-50 bg-black/50",
        "transition-opacity duration-300",
        isAnimating ? "opacity-100" : "opacity-0",
        overlayClassName,
      ),
    [isAnimating, overlayClassName],
  );

  const containerClasses = useMemo(
    () =>
      clsx(
        "fixed right-0 bottom-0 left-0 z-50 flex flex-col rounded-t-2xl bg-white",
        "transform transition-all duration-300",
        isAnimating ? "translate-y-0 ease-out" : "translate-y-full ease-in",
        className,
      ),
    [isAnimating, className],
  );

  if (!(shouldRender && isMobile) || typeof window === "undefined") {
    return null;
  }

  return createPortal(
    <>
      <div className={overlayClasses} onClick={onClose} />
      <div className={containerClasses}>
        <div
          className={clsx(
            "flex shrink-0 items-center justify-between px-4 py-5",
            borderClassName,
            headerClassName,
          )}
        >
          <div className="relative flex w-full items-center justify-center">
            <span
              className={clsx(
                "w-full text-center text-lg leading-26 font-semibold",
                titleClassName,
              )}
            >
              {title}
            </span>
            <button
              onClick={onClose}
              className={clsx(
                "absolute top-[1px] right-0",
                closeButtonClassName,
              )}
            >
              <FontAwesomeIcon
                icon={byPrefixAndName.fak["close-new"]}
                className="max-w-[24px] text-[24px]"
              />
            </button>
          </div>
        </div>
        <div
          className={clsx("flex-1 overflow-y-auto", contentClassName)}
          data-scroll-container
        >
          <div className={clsx("px-4 py-2", innerClassName)}>{children}</div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default BottomSheet;
